import React, { useMemo, useRef, useState } from 'react';
import {
  Box,
  Autocomplete,
  Typography,
  InputLabel,
  FormHelperText,
} from '@mui/material';

import { RiMapPin2Line } from 'react-icons/ri';
import {
  LOCATION_TYPE,
  RENTAL_WIDGET_FIELDS,
  ERRORS_PREFIX,
} from 'lib/utils/mainWidgetConstants';
import {
  NewAutoCompleteMenu,
 NewMainWidgetTextField,
} from 'views/rebranded-module/common/components/UI/NewMainWidget';
import StyledBox, {
  StyledIconedBox,
} from 'views/rebranded-module/common/components/UI/NewMainWidget/NewWidgetLocationField/NewWidgetLocationField.styles';
import { useContent } from 'views/rebranded-module/common/context/useContent';

const NewWidgetLocationField = (props) => {
  const {
    selectedLocationId,
    pickup,
    locationsList = [],
    label,
    placeholder,
    handleInputChange,
    handleLocationChange,
    handleRenderAllWidget,
    loading,
    errors,
    disabled,
    className,
    vertical,
    isDealWidget,
  } = props;
  const cmsData = useContent('NewMainWidgetComponent');
  const getFieldType = () => (pickup === LOCATION_TYPE.PICKUP
    ? RENTAL_WIDGET_FIELDS.PICKUP_LOCATION_ID
    : RENTAL_WIDGET_FIELDS.DROPOFF_LOCATION_ID);
  const [isFocused, setIsFocused] = useState(false);
  const autoCompleteInputRef = useRef<HTMLInputElement>(null);
  const selectedLocation = useMemo(
    () => locationsList.find(
      (location) => location.id && location.id === selectedLocationId,
    ) || null,
    [selectedLocationId, locationsList],
  );

  const placeHolder = useMemo(() => {
    if (isFocused && selectedLocation) {
      return `${selectedLocation.display_name}, ${selectedLocation.region_name}`;
    }
    return placeholder;
  }, [selectedLocation, isFocused, placeholder]);

  const fieldValue = useMemo(() => {
    if (isFocused) return null;
    return selectedLocation;
  }, [isFocused, selectedLocation]);
  const kind = pickup === LOCATION_TYPE.PICKUP ? 'pickup' : 'dropoff';
  const optionTestId = (option) => {
    const optionLabel = `${option.display_name}, ${option.region_name}`;
    return `${kind}-location-option-${encodeURIComponent(optionLabel)}`;
  };
  return (
    <StyledBox
      className={className}
      bordered={vertical}
      error={!!errors[`${ERRORS_PREFIX}${getFieldType()}`]}>
      <Box className="NWLFInputContainer">
        <StyledIconedBox
          disabled={disabled}
          className="WidgeLocaionField"
          Icon={RiMapPin2Line}
          error={!!errors[`${ERRORS_PREFIX}${getFieldType()}`]}>
          <Autocomplete
            value={fieldValue}
            options={loading ? [] : locationsList}
            filterOptions={(x) => x}
            getOptionLabel={(option) => `${option.display_name}, ${option.region_name}`}
            id={`${ERRORS_PREFIX}${getFieldType()}`}
            popupIcon={null}
            className="WidgetInput"
            autoComplete
            disableClearable
            includeInputInList
            PaperComponent={NewAutoCompleteMenu}
            onFocus={() => setIsFocused(true)}
            onBlur={() => setIsFocused(false)}
            onInputChange={(e, value, reason) => {
              if (reason !== 'reset') handleInputChange(pickup, value);
            }}
            disabled={disabled}
            onChange={(e, value) => {
              setIsFocused(true);
              handleLocationChange(getFieldType(), value?.id);
              autoCompleteInputRef?.current?.blur();
            }}
            onOpen={() => handleRenderAllWidget?.(true)}
            loading={loading}
            renderOption={(params, option) => (
              <li {...params} data-testid={optionTestId(option)}>
                <Box>
                  <Typography
                    dangerouslySetInnerHTML={{
                      __html: `${option.display_name}, <b>${option.region_name}</b>`,
                    }}
                  />
                  {option.distance >= 0 && (
                    <Typography>
                      {`${option.distance} ${cmsData.milesAway}${option.formattedAddress}`}
                    </Typography>
                  )}
                </Box>
              </li>
            )}
            renderInput={(params) => (
              <>
                <InputLabel className="WidgetInputLabel">{label}</InputLabel>
                <NewMainWidgetTextField
                  {...params}
                  inputProps={{
                    ...params.inputProps,
                    'data-testid': `${kind}-location-input`,
                  }}
                  inputRef={autoCompleteInputRef}
                  placeholder={placeHolder}
                  variant="filled"
                  error={!!errors[`${ERRORS_PREFIX}${getFieldType()}`]}
                />
              </>
            )}
          />
        </StyledIconedBox>
      </Box>
      {isDealWidget
        && pickup === LOCATION_TYPE.DROPOFF
        && errors[`${ERRORS_PREFIX}${getFieldType()}`] && (
          <FormHelperText error>
            {cmsData.dropoffLocationError}
          </FormHelperText>
      )} 
       </StyledBox>
  );
};

export { NewWidgetLocationField };